import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
    {
        q: "¿Qué es un test de drogas en saliva LYHER?",
        a: "Es un dispositivo rápido de detección cualitativa de múltiples drogas en fluido oral. El LYHER® Oral Fluid Multi-Drug Test Kit (Cube) permite un screening visual, simple y portátil, sin necesidad de grandes equipos.",
    },
    {
        q: "¿Qué muestra utiliza el LYHER Oral Fluid Multi-Drug Test Kit?",
        a: "Utiliza saliva (fluido oral), recolectada de forma simple con el hisopo incorporado en el colector del dispositivo.",
    },
    {
        q: "¿Qué drogas detecta?",
        a: "Detecta seis analitos: OPI (Opiáceos, cut-off 40 ng/mL), COC (Cocaína, 20 ng/mL), AMP (Anfetamina, 50 ng/mL), THC (Cannabis, 40 ng/mL), MET (Metanfetamina, 50 ng/mL) y PCP (Fenciclidina, 10 ng/mL).",
    },
    {
        q: "¿El test LYHER cuenta con FDA 510(k)?",
        a: "Sí. El LYHER® Oral Fluid Multi-Drug Test Kit (Cube) es una tecnología respaldada por autorización FDA 510(k).",
    },
    {
        q: "¿Cómo funciona el indicador de saturación?",
        a: "El colector incorpora un indicador visual de saturación que cambia de blanco a rojo, permitiendo verificar que la muestra de fluido oral fue recolectada correctamente antes de insertar el colector en el dispositivo.",
    },
    {
        q: "¿Cuánto demora el procedimiento?",
        a: "El procedimiento es simple de principio a fin: recolectar la muestra, verificar el indicador de saturación, insertar el colector, esperar y leer. Los resultados son visuales en minutos.",
    },
    {
        q: "¿Dónde encontrar LYHER en Chile?",
        a: "El test LYHER está disponible en Chile a través de sus canales comerciales: DETEX (www.detex.cl) y CINCO SALUD (www.cincosalud.cl).",
    },
    {
        q: "¿Dónde comprar o cotizar LYHER en Chile?",
        a: (
            <>
                Puedes cotizar directamente con los canales oficiales:{" "}
                <a href="mailto:ventas@detex.cl" className="font-semibold text-[#059669] hover:underline">ventas@detex.cl</a> o{" "}
                <a href="mailto:ventas@cincosalud.cl" className="font-semibold text-[#059669] hover:underline">ventas@cincosalud.cl</a>.
            </>
        ),
    },
    {
        q: "¿Cómo contactar a Detex?",
        a: (
            <>
                DETEX — Soluciones de prevención, seguridad laboral y detección para organizaciones. Sitio web:{" "}
                <a href="https://www.detex.cl" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#059669] hover:underline">www.detex.cl</a>, teléfono:{" "}
                <a href="tel:+56967273223" className="font-semibold text-[#059669] hover:underline">+56 9 6727 3223</a>, correo:{" "}
                <a href="mailto:ventas@detex.cl" className="font-semibold text-[#059669] hover:underline">ventas@detex.cl</a>.
            </>
        ),
    },
    {
        q: "¿Cómo contactar a Cinco Salud?",
        a: (
            <>
                CINCO SALUD — Soluciones de test rápidos y tecnología aplicada a salud y seguridad. Sitio web:{" "}
                <a href="https://www.cincosalud.cl" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#059669] hover:underline">www.cincosalud.cl</a>, teléfono:{" "}
                <a href="tel:+56990119887" className="font-semibold text-[#059669] hover:underline">+56 9 9011 9887</a>, correo:{" "}
                <a href="mailto:ventas@cincosalud.cl" className="font-semibold text-[#059669] hover:underline">ventas@cincosalud.cl</a>.
            </>
        ),
    },
];

export default function Faq() {
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" data-testid="faq-section" aria-labelledby="faq-title" className="relative bg-white py-28 md:py-36">
            <div className="mx-auto max-w-4xl px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                >
                    <p className="mb-5 text-[11px] font-bold tracking-[0.28em] text-[#059669]">PREGUNTAS FRECUENTES</p>
                    <h2 id="faq-title" className="font-display text-4xl font-extrabold tracking-tighter text-[#0A192F] sm:text-5xl">
                        DUDAS <span className="text-[#57A632]">FRECUENTES.</span>
                    </h2>
                </motion.div>
                <div className="mt-14 divide-y divide-slate-100 border-y border-slate-100">
                    {FAQS.map((f, i) => (
                        <div key={f.q}>
                            <button
                                type="button"
                                data-testid={`faq-question-${i + 1}`}
                                aria-expanded={open === i}
                                aria-controls={`faq-answer-${i + 1}`}
                                onClick={() => setOpen(open === i ? -1 : i)}
                                className="flex w-full items-center justify-between gap-6 py-6 text-left"
                            >
                                <span className="font-display text-base font-bold tracking-tight text-[#0A192F] sm:text-lg">
                                    {f.q}
                                </span>
                                <Plus
                                    size={18}
                                    className={`shrink-0 transition-[transform,color] duration-300 ${
                                        open === i ? "rotate-45 text-[#059669]" : "text-slate-400"
                                    }`}
                                />
                            </button>
                            <div
                                id={`faq-answer-${i + 1}`}
                                className={`grid transition-[grid-template-rows] duration-300 ${
                                    open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                                }`}
                            >
                                <div className="overflow-hidden">
                                    <p className="pb-6 pr-8 text-sm leading-relaxed text-slate-500 sm:text-base">{f.a}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
